import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Loading, EmptyState } from "@/components/common";
import { fmtMeetingDate, MEETING_TYPE_LABELS } from "@/lib/meetingsApi";
import { Landmark, CalendarDays, MapPin, Radio, ChevronRight, History } from "lucide-react";

function MeetingCard({ m, live }) {
  return (
    <Link to={`/forum/meetings/${m.meeting_id}`} data-testid="forum-meeting-card"
      className={`block rounded-2xl border p-5 transition-all hover:shadow-elevated ${live ? "border-brand-gold bg-brand-gold-50" : "border-brand-line bg-white shadow-card hover:border-brand-civic/40"}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          {live && <span className="inline-flex items-center gap-1 rounded-full bg-brand-gold px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wide text-[#5c4405] mb-2"><Radio className="h-3 w-3" /> Live Meeting</span>}
          <div className="text-xs font-semibold uppercase tracking-wider text-brand-civic">{m.governing_body_name}</div>
          <h3 className="font-display text-lg font-extrabold text-brand-ink mt-0.5 truncate">{m.title}</h3>
          <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-500">
            <span className="flex items-center gap-1.5"><CalendarDays className="h-4 w-4" /> {fmtMeetingDate(m.date, m.start_time)}</span>
            {m.location && <span className="flex items-center gap-1.5"><MapPin className="h-4 w-4" /> {m.location}</span>}
            <span>{MEETING_TYPE_LABELS[m.meeting_type] || m.meeting_type}</span>
          </div>
        </div>
        <span className={`inline-flex items-center gap-1 rounded-lg px-4 py-2 text-sm font-bold shrink-0 ${live ? "bg-brand-navy text-white" : "bg-brand-bg text-brand-navy border border-brand-line"}`}>
          {live ? "Join Meeting" : "Open Meeting"} <ChevronRight className="h-4 w-4" />
        </span>
      </div>
    </Link>
  );
}

export default function MyForum() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => { api.get("/forum/home").then((r) => setData(r.data)).catch(() => setErr(true)); }, []);

  if (err) return <EmptyState icon={Landmark} title="Forum unavailable" description="Please try again shortly." />;
  if (!data) return <Loading label="Loading your forum…" />;
  if (!data.is_board_user)
    return <EmptyState icon={Landmark} title="No Forum access"
      description="Forum View is for authorized members of a governing body. If you believe this is an error, contact your clerk or administrator." />;

  return (
    <div data-testid="my-forum" className="space-y-8">
      <div>
        <h1 className="font-display text-3xl font-extrabold tracking-tight text-brand-ink">My Forum</h1>
        <p className="text-slate-500 mt-1">Your meetings, agendas, documents, and voting.</p>
      </div>

      {data.current.length > 0 && (
        <section>
          <h2 className="text-sm font-bold uppercase tracking-wider text-brand-gold mb-3 flex items-center gap-2"><Radio className="h-4 w-4" /> Happening Now</h2>
          <div className="space-y-4" data-testid="forum-current">{data.current.map((m) => <MeetingCard key={m.meeting_id} m={m} live />)}</div>
        </section>
      )}

      <section>
        <h2 className="text-sm font-bold uppercase tracking-wider text-brand-civic mb-3 flex items-center gap-2"><CalendarDays className="h-4 w-4" /> Upcoming Meetings</h2>
        {data.upcoming.length === 0
          ? <EmptyState icon={CalendarDays} title="No upcoming meetings" description="Scheduled meetings for your governing body will appear here." />
          : <div className="space-y-4" data-testid="forum-upcoming">{data.upcoming.map((m) => <MeetingCard key={m.meeting_id} m={m} />)}</div>}
      </section>

      {data.recent.length > 0 && (
        <section>
          <h2 className="text-sm font-bold uppercase tracking-wider text-slate-500 mb-3 flex items-center gap-2"><History className="h-4 w-4" /> Recent Meetings</h2>
          <div className="space-y-4" data-testid="forum-recent">{data.recent.map((m) => <MeetingCard key={m.meeting_id} m={m} />)}</div>
        </section>
      )}
    </div>
  );
}
